import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserPlus, CheckCircle2, XCircle, Clock, Mail, Send, Filter, ChevronDown,
  ChevronUp, Building2, Phone, FileText, Copy, ExternalLink, Award, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Pending', icon: Clock },
  approved: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Approved', icon: CheckCircle2 },
  rejected: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Rejected', icon: XCircle },
};

const CHAMBER_ROLES = [
  { value: 'ambassador', label: 'Ambassador', desc: 'Represents the Chamber externally' },
  { value: 'advisor', label: 'Advisor', desc: 'Strategic guidance and counsel' },
  { value: 'counselor', label: 'Counselor', desc: 'Member support and mentoring' },
  { value: 'contributor', label: 'Contributor', desc: 'Active contributor to Chamber initiatives' },
  { value: 'liaison', label: 'Liaison', desc: 'Connects the Chamber with partners' },
];

const MembershipApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [selectedChamberRole, setSelectedChamberRole] = useState({});
  const [assignType, setAssignType] = useState({}); // 'membership' or 'chamber_role'
  const [rejectReason, setRejectReason] = useState({});
  const [processing, setProcessing] = useState({});
  const [foundingMember, setFoundingMember] = useState({});

  useEffect(() => { fetchData(); }, [filter]);

  const fetchData = async () => {
    try {
      const [appsRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/applications${filter ? `?status=${filter}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/membership-plans/all`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setApplications(appsRes.data);
      setPlans(plansRes.data.filter(p => p.is_active));
    } catch { /* */ } finally { setLoading(false); }
  };

  const handleApprove = async (appId) => {
    const type = assignType[appId] || 'membership';
    const planId = type === 'membership' ? selectedPlan[appId] : '';
    const chamberRole = type === 'chamber_role' ? selectedChamberRole[appId] : '';

    if (type === 'membership' && !planId) {
      toast.error('Please select a membership plan before approving');
      return;
    }
    if (type === 'chamber_role' && !chamberRole) {
      toast.error('Please select a chamber role before approving');
      return;
    }

    setProcessing(prev => ({ ...prev, [appId]: true }));
    const badges = foundingMember[appId] ? ['founding_member'] : [];
    try {
      await axios.put(`${API}/admin/applications/${appId}/approve`, {
        plan_id: planId,
        chamber_role: chamberRole,
        badges,
      }, { withCredentials: true });
      toast.success('Application approved! Invitation sent.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(prev => ({ ...prev, [appId]: false }));
    }
  };

  const handleReject = async (appId) => {
    setProcessing(prev => ({ ...prev, [appId]: true }));
    try {
      await axios.put(`${API}/admin/applications/${appId}/reject`, { reason: rejectReason[appId] || '' }, { withCredentials: true });
      toast.success('Application rejected');
      fetchData();
    } catch { toast.error('Failed to reject'); }
    finally { setProcessing(prev => ({ ...prev, [appId]: false })); }
  };

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-applications">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Membership Applications</h1>
          <p className="text-slate-600 text-sm mt-1">Review, approve, and assign membership plans or chamber roles.</p>
        </div>
        {pendingCount > 0 && (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-sm text-sm font-semibold">{pendingCount} pending</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2 mb-6">
        <Filter className="w-4 h-4 text-slate-500" />
        {['pending', 'approved', 'rejected', ''].map(f => (
          <button key={f} onClick={() => { setFilter(f); setLoading(true); }}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            data-testid={`filter-${f || 'all'}`}
          >
            {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : applications.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <UserPlus className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium">No {filter} applications</p>
        </div>
      ) : (
        <div className="space-y-3">
          {applications.map((app, index) => {
            const status = statusConfig[app.status] || statusConfig.pending;
            const isExpanded = expandedId === app.id;
            const currentType = assignType[app.id] || 'membership';
            return (
              <motion.div key={app.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * index }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`application-${index}`}
              >
                <div className="p-4 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(isExpanded ? null : app.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-[#002249] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {app.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#002249] text-sm">{app.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <span className="flex items-center"><Mail className="w-3 h-3 mr-1" />{app.email}</span>
                          {app.company && <span className="flex items-center"><Building2 className="w-3 h-3 mr-1" />{app.company}</span>}
                          <span>{new Date(app.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {app.chamber_role && (
                        <span className="text-[10px] font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-sm capitalize">{app.chamber_role}</span>
                      )}
                      {app.assigned_plan_id && !app.chamber_role && (
                        <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm">Paid Member</span>
                      )}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border flex items-center ${status.color}`}>
                        <status.icon className="w-3 h-3 mr-1" />{status.label}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-200">
                    <div className="p-4 bg-slate-50 grid md:grid-cols-2 gap-3 text-sm">
                      <div><span className="font-medium text-slate-600">Name:</span> {app.name}</div>
                      <div><span className="font-medium text-slate-600">Email:</span> {app.email}</div>
                      <div><span className="font-medium text-slate-600">Phone:</span> {app.phone || '—'}</div>
                      <div><span className="font-medium text-slate-600">Company:</span> {app.company || '—'}</div>
                      <div className="md:col-span-2"><span className="font-medium text-slate-600">Interest:</span> {app.interest || '—'}</div>
                      <div className="md:col-span-2"><span className="font-medium text-slate-600">Notes:</span> {app.notes || '—'}</div>
                      {app.approved_by_name && <div><span className="font-medium text-slate-600">Approved by:</span> {app.approved_by_name} on {new Date(app.approved_at).toLocaleDateString()}</div>}
                      {app.chamber_role && <div><span className="font-medium text-slate-600">Chamber Role:</span> <span className="capitalize font-semibold text-indigo-700">{app.chamber_role}</span></div>}
                      {app.rejection_reason && <div className="md:col-span-2"><span className="font-medium text-slate-600">Rejection reason:</span> {app.rejection_reason}</div>}
                    </div>

                    {app.status === 'pending' && (
                      <div className="p-4 bg-amber-50/50 border-t border-slate-200 space-y-4">
                        {/* Assignment Type Toggle */}
                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-2">Assignment Type *</label>
                          <div className="flex rounded-lg overflow-hidden border border-slate-200 w-fit" data-testid={`assign-type-${index}`}>
                            <button
                              onClick={() => setAssignType(prev => ({ ...prev, [app.id]: 'membership' }))}
                              className={`px-4 py-2 text-sm font-medium transition-all ${currentType === 'membership' ? 'bg-[#002249] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                              data-testid={`type-membership-${index}`}
                            >
                              Membership Plan
                            </button>
                            <button
                              onClick={() => setAssignType(prev => ({ ...prev, [app.id]: 'chamber_role' }))}
                              className={`px-4 py-2 text-sm font-medium border-l border-slate-200 transition-all ${currentType === 'chamber_role' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                              data-testid={`type-chamber-${index}`}
                            >
                              Chamber Role
                            </button>
                          </div>
                        </div>

                        {/* Membership Plan Selector */}
                        {currentType === 'membership' && (
                          <div>
                            <label className="text-xs font-semibold text-slate-700 block mb-1">Membership Plan (Paid) *</label>
                            <select value={selectedPlan[app.id] || ''} onChange={e => setSelectedPlan(prev => ({ ...prev, [app.id]: e.target.value }))}
                              className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20" data-testid={`plan-select-${index}`}
                            >
                              <option value="">Select a plan...</option>
                              {plans.map(p => <option key={p.id} value={p.id}>{p.name} — {p.price}</option>)}
                            </select>
                            <p className="text-[11px] text-slate-400 mt-1">Paid membership with directory listing and full benefits.</p>
                          </div>
                        )}

                        {/* Chamber Role Selector */}
                        {currentType === 'chamber_role' && (
                          <div>
                            <label className="text-xs font-semibold text-indigo-700 block mb-1">Chamber Role (Non-Paid) *</label>
                            <select value={selectedChamberRole[app.id] || ''} onChange={e => setSelectedChamberRole(prev => ({ ...prev, [app.id]: e.target.value }))}
                              className="w-full border border-indigo-200 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-indigo-50/30" data-testid={`chamber-role-select-${index}`}
                            >
                              <option value="">Select a role...</option>
                              {CHAMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                            </select>
                            <p className="text-[11px] text-indigo-400 mt-1">Non-paid chamber participant with community access.</p>
                          </div>
                        )}

                        {/* Founding Member Badge */}
                        <label className="flex items-center gap-2.5 cursor-pointer py-1" data-testid={`founding-toggle-${index}`}>
                          <button type="button" onClick={() => setFoundingMember(prev => ({ ...prev, [app.id]: !prev[app.id] }))}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${foundingMember[app.id] ? 'bg-amber-500 border-amber-500' : 'border-slate-300'}`}>
                            {foundingMember[app.id] && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </button>
                          <span className="text-sm text-slate-700">Assign <span className="font-semibold text-amber-700">Founding Member</span> badge</span>
                        </label>

                        {/* Action Buttons */}
                        <div className="flex space-x-3">
                          <Button
                            onClick={() => handleApprove(app.id)}
                            disabled={processing[app.id] || (currentType === 'membership' && !selectedPlan[app.id]) || (currentType === 'chamber_role' && !selectedChamberRole[app.id])}
                            className="bg-green-600 hover:bg-green-700 rounded-sm" data-testid={`approve-app-${index}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />{processing[app.id] ? 'Processing...' : 'Approve & Send Invite'}
                          </Button>
                          <Button variant="outline" onClick={() => handleReject(app.id)} disabled={processing[app.id]}
                            className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm" data-testid={`reject-app-${index}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />Reject
                          </Button>
                        </div>
                      </div>
                    )}

                    {app.status === 'approved' && app.invite_sent && (
                      <div className="p-3 bg-green-50 border-t border-slate-200 text-sm text-green-700 flex items-center">
                        <Send className="w-4 h-4 mr-2" /> Invitation email sent to {app.email}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MembershipApplicationsPage;
